import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { useNavigate } from 'react-router-dom';
import { Truck, Search, Calendar, User, FileText, MapPin, Info, Plus } from 'lucide-react';
import Spinner from './Spinner.js';

const PickupChallanList = ({ db, user }) => {
  const [loading, setLoading] = useState(true);
  const [pickups, setPickups] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchPickups();
  }, []);

  const fetchPickups = async () => {
    setLoading(true);
    try {
      // For now, we fetch from a 'dispatches' or similar collection filtering for Inbound types
      // Or we might have a dedicated 'pickups' collection. 
      // Based on the brainstorm, let's assume 'pickups' for now or 'dispatches' with a specific type.
      // We'll use 'pickups' as a placeholder for the new inwards flow.
      const q = query(
        collection(db, "pickups"),
        orderBy("createdAt", "desc")
      );
      
      const snapshot = await getDocs(q);
      const pickupData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPickups(pickupData);
    } catch (error) {
      console.error("Error fetching pickup challans: ", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPickups = pickups.filter(p => 
    p.pcNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tableHeaderStyle = {
    padding: '12px 15px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase',
    borderBottom: '1px solid #e5e7eb'
  };

  const tableCellStyle = {
    padding: '12px 15px',
    fontSize: '14px',
    color: '#1e293b',
    borderBottom: '1px solid #e5e7eb'
  };

  const getStatusBadge = (status) => {
    const isConfirmed = status === 'Confirmed';
    return (
      <span style={{ 
        padding: '4px 12px', borderRadius: '12px', fontSize: '11px', fontWeight: '700',
        textTransform: 'uppercase',
        backgroundColor: isConfirmed ? '#dcfce7' : '#f1f5f9', 
        color: isConfirmed ? '#166534' : '#475569',
        border: `1px solid ${isConfirmed ? '#bbf7d0' : '#e2e8f0'}`
      }}>
        {status || 'Draft'}
      </span>
    );
  };

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}><Spinner /></div>;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Truck size={24} color="#2563eb" /> Pickup Challan (PC)
          </h2>
          <p style={{ color: '#64748b', marginTop: '5px' }}>Manage all inbound material pickup challans.</p>
        </div>
        <button 
          onClick={() => navigate('/ops/pickup-challan/new')}
          style={{
            backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '10px 20px',
            borderRadius: '6px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px'
          }}
        >
          <Plus size={18} /> Initiate Pickup
        </button>
      </div>

      <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ position: 'relative', maxWidth: '400px' }}>
            <Search size={18} color="#9ca3af" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
            <input 
              type="text" 
              placeholder="Search by PC No, Customer, or Order..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%', padding: '10px 10px 10px 40px', borderRadius: '8px', border: '1px solid #d1d5db',
                outline: 'none', fontSize: '14px'
              }}
            />
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc' }}>
                <th style={tableHeaderStyle}>PC Date</th>
                <th style={tableHeaderStyle}>Customer</th>
                <th style={tableHeaderStyle}>Order No.</th>
                <th style={tableHeaderStyle}>Type</th>
                <th style={tableHeaderStyle}>Warehouse</th>
                <th style={tableHeaderStyle}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredPickups.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                    No pickup challans found.
                  </td>
                </tr>
              ) : (
                filteredPickups.map((p) => (
                  <tr 
                    key={p.id} 
                    onClick={() => navigate(`/ops/pickup-challan/${p.id}`)}
                    style={{ cursor: 'pointer' }}
                    className="hover-highlight"
                  >
                    <td style={tableCellStyle}>{p.createdAt?.toDate ? p.createdAt.toDate().toLocaleDateString('en-IN') : 'N/A'}</td>
                    <td style={tableCellStyle}>
                      <div style={{ fontWeight: '600' }}>{p.customerName || 'N/A'}</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{p.pcNo}</div>
                    </td>
                    <td style={tableCellStyle}>{p.orderNumber || p.referenceNo || 'N/A'}</td>
                    <td style={tableCellStyle}>
                      <span style={{ 
                        padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: '600',
                        backgroundColor: p.pcType === 'Rental' ? '#eff6ff' : '#fef2f2',
                        color: p.pcType === 'Rental' ? '#2563eb' : '#dc2626'
                      }}>
                        {p.pcType || 'N/A'}
                      </span>
                    </td>
                    <td style={tableCellStyle}>{p.warehouse || 'N/A'}</td>
                    <td style={tableCellStyle}>
                      {getStatusBadge(p.status)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PickupChallanList;